import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLive } from "@/lib/useLive";
import { formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, MessageCircleQuestion, Send } from "lucide-react";

function Thread({ t, onReplied }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const open = t.status === "open";
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/business/questions/${t.id}/reply`, { text: text.trim() });
      toast.success("Respuesta enviada. Le avisamos al alumno por correo.");
      setText(""); onReplied();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className={`p-4 space-y-3 ${open ? "border-primary/40" : ""}`} data-testid={`question-thread-${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-bold break-words">{t.customer_name || "Alumno"}</p>
          <p className="text-xs text-muted-foreground break-all">{t.customer_email}</p>
          <p className="text-xs text-primary font-semibold mt-0.5 break-words">{t.workshop_name}</p>
        </div>
        <Badge className={open ? "bg-coral text-white hover:bg-coral" : ""} variant={open ? "default" : "outline"} data-testid={`question-status-${t.id}`}>
          {open ? "Por responder" : "Respondida"}
        </Badge>
      </div>
      <div className="space-y-2 rounded-xl bg-muted/40 p-3 max-h-72 overflow-y-auto">
        {t.messages.map((m, i) => (
          <div key={i} className={m.sender === "maestro" ? "text-right" : "text-left"}>
            <span className={`inline-block max-w-[85%] text-left rounded-2xl px-3 py-1.5 text-sm break-words ${m.sender === "maestro" ? "bg-primary text-primary-foreground" : "bg-background border"}`}>{m.text}</span>
            <p className="text-[10px] text-muted-foreground mt-0.5">{m.sender === "maestro" ? "Tú" : t.customer_name || "Alumno"} · {formatDateTime(m.created_at)}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2 items-end">
        <Textarea rows={2} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe tu respuesta…" data-testid={`question-reply-input-${t.id}`} />
        <Button size="icon" onClick={send} disabled={busy || !text.trim()} aria-label="Enviar respuesta" data-testid={`question-reply-send-${t.id}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </Card>
  );
}

export function QuestionsInbox({ onCount }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/business/questions").then((r) => {
    setRows(r.data); onCount?.(r.data.filter((t) => t.status === "open").length);
  }), [onCount]);
  useEffect(() => { load(); }, [load]);
  useLive(load, 10000);
  if (!rows) return <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mt-10" />;
  const open = rows.filter((t) => t.status === "open").length;
  return (
    <div className="mt-6 space-y-4" data-testid="questions-inbox">
      <div>
        <h3 className="text-xl font-extrabold flex items-center gap-2"><MessageCircleQuestion className="w-5 h-5 text-primary" />Preguntas de alumnos</h3>
        <p className="text-sm text-muted-foreground mt-1">{open ? `Tienes ${open} pregunta(s) por responder.` : "Estás al día con tus respuestas."} Las nuevas preguntas aparecen primero.</p>
      </div>
      {rows.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="questions-empty">Aún no recibes preguntas. Cuando un alumno pregunte desde la ficha de un taller, aparecerá aquí.</Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">{rows.map((t) => <Thread key={t.id} t={t} onReplied={load} />)}</div>
      )}
    </div>
  );
}
